import { useCallback, useRef, useState } from 'react';

import { Drawer } from '../../../../components/Drawer';
import Dropdown from '../../../../components/Dropdown';
import { FormList, FormListFieldError, FormListItem } from '../../../../components/FormList';
import { HelpDrawerLink } from '../../../../components/HelpDrawerLink';
import TextField from '../../../../components/TextField';
import { WorkflowSubStepProps } from '../../config';

export interface FederalDebtEntry {
  debtDetails: string;
}

export interface FederalDebtFormData {
  isAddingEntry?: boolean;
  hasFederalDebt: boolean | null;
  federalDebts: FormListItem<FederalDebtEntry>[];
  draftFederalDebt: FederalDebtEntry;
}

const yesNoOptions = [
  { label: '- Select an option -', value: null },
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

const getEmptyFederalDebt = (): FederalDebtEntry => ({
  debtDetails: '',
});

function FederalDebtPage({ data, fieldErrors, updateData }: WorkflowSubStepProps<FederalDebtFormData>) {
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const baseTestId = 'federal-debt-page';

  const handleFederalDebtChange = (value: boolean | null) => {
    if (value !== true) {
      updateData({
        ...data,
        draftFederalDebt: getEmptyFederalDebt(),
        federalDebts: [],
        hasFederalDebt: value,
        isAddingEntry: false,
      });
    } else {
      updateData({
        ...data,
        hasFederalDebt: value,
      });
    }
  };

  const pendingItemsRef = useRef<FormListItem<FederalDebtEntry>[] | null>(null);

  const handleItemsChange = useCallback(
    (items: FormListItem<FederalDebtEntry>[]) => {
      pendingItemsRef.current = items;
      updateData({
        ...data,
        federalDebts: items,
      });
    },
    [data, updateData],
  );

  const handleDraftChange = useCallback(
    (draft: FederalDebtEntry, isDraftActive?: boolean) => {
      const itemsToUse = pendingItemsRef.current !== null ? pendingItemsRef.current : data.federalDebts;
      pendingItemsRef.current = null;

      updateData({
        ...data,
        draftFederalDebt: draft,
        federalDebts: itemsToUse,
        isAddingEntry: isDraftActive ?? data.isAddingEntry,
      });
    },
    [data, updateData],
  );

  const handleDraftActiveChange = useCallback(
    (isActive: boolean) => updateData({ ...data, isAddingEntry: isActive }),
    [data, updateData],
  );

  const validateFederalDebt = useCallback((item: FederalDebtEntry): FormListFieldError => {
    const errors: FormListFieldError = {};

    if (!item.debtDetails) {
      errors.debtDetails = 'Details about the Federal debt are required';
    }

    return errors;
  }, []);

  const isDraftEmpty = useCallback((draft: FederalDebtEntry): boolean => {
    return !draft.debtDetails;
  }, []);

  const renderEditForm = useCallback(
    (item: FederalDebtEntry, onChange: (item: FederalDebtEntry) => void, errors: FormListFieldError) => {
      const handleFieldChange = (value: string) => {
        onChange({
          ...item,
          debtDetails: value,
        });
      };

      return (
        <div
          className='form-list__nested-form'
          data-testid={`${baseTestId}-entry-form`}>
          <div
            className='ds-u-margin-bottom--3'
            data-testid={`${baseTestId}-entry-details`}>
            <TextField
              multiline
              required
              data-testid={`${baseTestId}-entry-details-field`}
              errorMessage={errors.debtDetails}
              label='Details about the Federal debt'
              name='debtDetails'
              rows={5}
              tooltipText='Provide details including the date, explanation of the debt, place of occurrence, and the name and address of the Federal agency involved'
              value={item.debtDetails || ''}
              onChange={(e) => handleFieldChange(e.target.value)}
            />
          </div>
        </div>
      );
    },
    [],
  );

  const renderItemSummary = useCallback((item: FederalDebtEntry) => {
    const truncatedDetails =
      item.debtDetails.length > 50 ? `${item.debtDetails.substring(0, 50)}...` : item.debtDetails;

    return {
      details: '',
      title: truncatedDetails,
    };
  }, []);

  const renderReadOnlyItem = useCallback((item: FederalDebtEntry) => {
    return (
      <div className='form-list__nested-form'>
        <div className='form-list__readonly-field'>
          <span className='form-list__readonly-label'>Details</span>
          <span className='form-list__readonly-value'>{item.debtDetails}</span>
        </div>
      </div>
    );
  }, []);

  const draftFieldErrors: FormListFieldError = {
    debtDetails: fieldErrors?.['draftFederalDebt.debtDetails'],
  };

  return (
    <div data-testid={baseTestId}>
      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-has-federal-debt`}>
        <Dropdown
          required
          data-testid={`${baseTestId}-has-federal-debt-dropdown`}
          errorMessage={fieldErrors?.hasFederalDebt}
          label='Are you delinquent on any Federal debt?'
          name='hasFederalDebt'
          options={yesNoOptions}
          tooltipText='This question covers any delinquent Federal debts including taxes, student loans, and other obligations to Federal agencies.'
          value={data.hasFederalDebt}
          onChange={handleFederalDebtChange}
        />
      </div>

      {data.hasFederalDebt === true && (
        <div
          className='ds-u-margin-bottom--3'
          data-testid={`${baseTestId}-entries`}>
          <FormList<FederalDebtEntry>
            addLinkText='Add Another Federal Debt'
            data-testid={`${baseTestId}-entries-list`}
            draftItem={data.draftFederalDebt}
            fieldErrors={draftFieldErrors}
            getEmptyItem={getEmptyFederalDebt}
            isDraftActive={data.isAddingEntry}
            isDraftEmpty={isDraftEmpty}
            items={data.federalDebts}
            label='Federal Debt Details'
            renderEditForm={renderEditForm}
            renderItemSummary={renderItemSummary}
            renderReadOnlyItem={renderReadOnlyItem}
            tooltipText='Provide details about each Federal debt you are delinquent on.'
            validateItem={validateFederalDebt}
            onDraftActiveChange={handleDraftActiveChange}
            onDraftChange={handleDraftChange}
            onItemsChange={handleItemsChange}
          />
        </div>
      )}

      <div
        className='ds-u-border-top--2 ds-u-padding-top--3 ds-u-margin-top--4 field-max-width'
        data-testid={`${baseTestId}-help`}>
        <HelpDrawerLink
          data-testid={`${baseTestId}-help-link`}
          onClick={() => setIsHelpDrawerOpen(true)}
        />
      </div>

      <Drawer
        data-testid={`${baseTestId}-drawer`}
        heading='Help: Federal Debt'
        isOpen={isHelpDrawerOpen}
        onClose={() => setIsHelpDrawerOpen(false)}>
        <div className='ds-u-padding--3'>
          <h3 className='ds-u-margin-top--0'>Get Help with Federal Debt Details</h3>
          <p>
            This section asks whether you have any unpaid Federal debts that were not paid by the scheduled due date.
            This is required for the Declaration of Federal Employment (OF-306).
          </p>

          <h4 className='ds-u-margin-top--3'>What is a Federal Debt?</h4>
          <p>Common Federal debt types include:</p>
          <ul>
            <li>Federal taxes owed to the IRS</li>
            <li>Federal student loans that are not being paid back (&quot;in default&quot;)</li>
            <li>Debts to other Federal agencies</li>
            <li>Overpayments of Federal benefits</li>
          </ul>

          <h4 className='ds-u-margin-top--3'>What is &quot;Delinquent&quot;?</h4>
          <p>
            A debt is considered delinquent if you have not made payments as agreed or if the debt is in collection
            status.
          </p>

          {/* TODO: Restore the following guidance when ACP-792 is completed with these fields.
          <h4 className='ds-u-margin-top--3'>Steps you are taking to correct the error or repay the debt</h4>
          <p>Enter details about how you are correcting an error, or have planned to pay off the Federal debt.</p>
          <h4 className='ds-u-margin-top--3'>Type of Federal debt</h4>
          <p>Enter the type of Federal debt you are delinquent on. Examples of Federal Debt include:</p>
          <ul>
            <li>Federal Student Loans</li>
            <li>Federal Tax Debt/Liens</li>
            <li>Federal Housing Loans (mortgages guaranteed by the Federal Housing Administration (FHA))</li>
            <li>VA loans</li>
            <li>USDA loans</li>
            <li>SBA Loans</li>
            <li>Federal Fines/Penalties</li>
          </ul>
          <h4 className='ds-u-margin-top--3'>Length of Federal debt</h4>
          <p>
            Enter how long you have agreed to pay off the Federal debt. Or how long you expect it will take to pay off
            the Federal debt.
          </p>
          <h4 className='ds-u-margin-top--3'>Amount of the delinquency or default</h4>
          <p>Enter the total amount of the Federal debt. Include the amount you have already paid off.</p>
          */}
          <p className='ds-u-margin-top--3'>
            <strong>Note:</strong> If you have more than one Federal debt to detail, use the &quot;Add Another Federal
            Debt&quot; button to add each one.
          </p>
        </div>
      </Drawer>
    </div>
  );
}

export default FederalDebtPage;
